import { Typography, Modal, IconNode, Button } from '@components/base';
import { TextField } from '@components/common';
import { Controller, useForm } from 'react-hook-form';
import Images from '@assets/images';

import css from './index.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { addUserSchema } from '@helpers/yup';
import { generateSecurePassword } from '@helpers/utils';
import notify from '@helpers/toastify-helper';
import { createUser } from '@services/user.service';

interface AddUserFormValues {
  email: string;
  given_name: string;
  family_name: string;
  phone_number?: string;
}

type AddUserPayload = AddUserFormValues & { password: string };

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onFormSubmit?: (data: AddUserPayload) => void;
}

const AddUserModal = (props: AddUserModalProps) => {
  const { open, onClose } = props;
  const { control, handleSubmit } = useForm<AddUserFormValues>({
    resolver: yupResolver(addUserSchema),
    defaultValues: {
      email: '',
      given_name: '',
      family_name: '',
      phone_number: ''
    },
    shouldUnregister: true,
    shouldFocusError: true
  });

  const onSubmitHandler = handleSubmit(
    async (formData) => {
      const payload = {
        ...formData,
        password: generateSecurePassword(12)
      };
      const response = await createUser(payload);
      const isOk = response?.success === true;
      if (isOk) {
        notify({ message: 'User created successfully!' });
        if (props.onComplete) props.onComplete();
        onClose();
      } else {
        notify({
          title: 'We have little problem',
          message: response?.error || 'There was an error understanding your request.',
          severity: 'error'
        });
      }
      props.onFormSubmit?.(payload);
    },
    (errors) => {
      const firstError = Object.values(errors)[0];
      const message = (firstError as any)?.message ?? 'Please fix the highlighted fields';
    }
  );

  return (
    <Modal open={open} onClose={onClose}>
      <div className={css.modalWrapper}>
        <section className={css.modalHeader}>
          <Typography variant="h4">Add User</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>

        <form
          noValidate
          className={css.formWrapper}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSubmitHandler();
          }}
        >
          <section className={css.modalContent}>
            <div className={css.rowGroup}>
              <div className={css.fieldWrapper}>
                <Controller
                  name="given_name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      required
                      type="text"
                      inputMode="text"
                      autoComplete="given-name"
                      placeholder="First name"
                      label="First name"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      rootClassName={css.fieldSpacing}
                    />
                  )}
                />
              </div>
              <div className={css.fieldWrapper}>
                <Controller
                  name="family_name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      required
                      type="text"
                      inputMode="text"
                      autoComplete="family-name"
                      placeholder="Last name"
                      label="Last name"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      rootClassName={css.fieldSpacing}
                    />
                  )}
                />
              </div>
            </div>
            <div className={css.fieldWrapper}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="name@example.com"
                    label="Email"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    rootClassName={css.fieldSpacing}
                  />
                )}
              />
            </div>
            {/* Password field removed; password will be auto-generated on submit */}
            <div className={css.fieldWrapper}>
              <Controller
                name="phone_number"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="e.g. 8613469040410"
                    label="Phone number"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    rootClassName={css.fieldSpacing}
                  />
                )}
              />
            </div>
          </section>
          <section className={css.modalFooter}>
            <Button variant="outlined-secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={onSubmitHandler}>
              Submit
            </Button>
          </section>
        </form>
      </div>
    </Modal>
  );
};

export default AddUserModal;
